// File: frontend/src/components/policy/PolicyCommandCenter.js

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  FileText,
  Building2,
  CreditCard,
  Shield,
  Users,
  Car,
  Home,
  User,
  TrendingUp,
  Briefcase,
  Sparkles,
  MessageCircle,
  FileCheck,
  DollarSign,
  Trash2,
  Loader2,
  ChevronUp
} from 'lucide-react';
import styles from './PolicyCommandCenter.module.css';

const PolicyCommandCenter = ({ 
  metadata, 
  fileInfo,
  onStartPolicyChat,
  onStartClaimsChat,
  onStartQuoteChat,
  onDelete,
  isStartingPolicyChat,
  isStartingClaimsChat,
  isStartingQuoteChat
}) => {
  const [activeSection, setActiveSection] = useState('summary');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const sectionRefs = useRef({});
  const contentRef = useRef(null);

  // Build sections list based on available data
  const sections = useMemo(() => {
    const s = [
      { id: 'summary', label: 'Summary', icon: FileText, always: true },
      { id: 'policy-info', label: 'Policy', icon: FileText, always: true },
      { id: 'company', label: 'Insurer', icon: Building2, always: true },
      { id: 'premium', label: 'Premium', icon: CreditCard, always: true },
      { id: 'coverages', label: 'Coverage', icon: Shield, always: true },
    ];

    if (metadata.endorsements?.endorsements?.length > 0) {
      s.push({ id: 'endorsements', label: 'Endorsements', icon: FileText });
    }

    if (metadata.declined_coverages?.declined_items?.length > 0) {
      s.push({ id: 'declined', label: 'Declined', icon: XCircle });
    }

    s.push({ id: 'insured', label: 'Insured', icon: User, always: true });

    if (metadata.vehicles?.vehicles?.length > 0) {
      s.push({ id: 'vehicles', label: 'Vehicles', icon: Car });
    }

    if (metadata.properties?.properties?.length > 0) {
      s.push({ id: 'properties', label: 'Properties', icon: Home });
    }

    if (metadata.drivers?.drivers?.length > 0) {
      s.push({ id: 'drivers', label: 'Drivers', icon: Users });
    }

    if (metadata.rating_information) {
      s.push({ id: 'rating', label: 'Rating', icon: TrendingUp });
    }

    if (metadata.claims_history && (metadata.claims_history.chargeable_claims?.length > 0 || metadata.claims_history.conviction_history?.length > 0)) {
      s.push({ id: 'claims-history', label: 'Claims', icon: AlertCircle });
    }

    if (metadata.agent_information) {
      s.push({ id: 'agent', label: 'Agent', icon: Briefcase });
    }

    if (metadata.additional_details) {
      s.push({ id: 'additional', label: 'More', icon: Sparkles });
    }

    if (metadata.extraction_notes) {
      s.push({ id: 'notes', label: 'Notes', icon: AlertCircle });
    }

    return s;
  }, [metadata]);

  // Scroll spy
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);

      const scrollPosition = window.scrollY + 120;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const element = sectionRefs.current[section.id];
        if (element) {
          const { offsetTop } = element;
          if (scrollPosition >= offsetTop) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = useCallback((sectionId) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const setSectionRef = useCallback((id) => (el) => {
    if (el) {
      sectionRefs.current[id] = el;
    }
  }, []);

  const formatCoverageType = (type) => {
    if (!type) return 'N/A';
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatCoverageCategory = (category) => {
    if (!category) return 'N/A';
    return category.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // ==================== RENDER SECTIONS ====================

  const renderSummary = () => {
    const policyType = formatCoverageType(metadata.policy_metadata?.policy_type);
    const status = formatCoverageType(metadata.policy_metadata?.policy_status);
    const company = metadata.insurance_company?.company_name;
    const premium = metadata.premium_billing?.total_premium;
    const effectiveDate = metadata.policy_metadata?.effective_date;
    const expiryDate = metadata.policy_metadata?.expiry_date;
    const policyNumber = metadata.policy_metadata?.policy_number;
    const vehicleCount = metadata.vehicles?.vehicle_count || 0;
    const driverCount = metadata.drivers?.driver_count || 0;
    const coverageCount = metadata.coverages?.coverage_count || metadata.coverages?.coverages?.length || 0;

    const isActive = status?.toLowerCase() === 'active';

    return (
      <div ref={setSectionRef('summary')} id="summary" className={styles.summarySection}>
        <div className={styles.summaryGrid}>
          <div className={styles.summaryMain}>
            <div className={styles.summaryTopRow}>
              <div className={styles.policyTypeBadge}>
                {policyType || 'Insurance'} Policy
              </div>
              <div className={`${styles.statusBadge} ${isActive ? styles.statusActive : styles.statusInactive}`}>
                <span className={styles.statusDot} />
                {status || 'Unknown'}
              </div>
            </div>
            
            {company && (
              <h2 className={styles.companyName}>{company}</h2>
            )}
            
            {policyNumber && (
              <p className={styles.policyNumber}>Policy #{policyNumber}</p>
            )}
          </div>

          <div className={styles.summaryStats}>
            {premium && (
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Total Premium</span>
                <span className={styles.statValue}>{formatCurrency(premium)}</span>
              </div>
            )}
            
            {effectiveDate && expiryDate && (
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Policy Period</span>
                <span className={styles.statValueSmall}>
                  {formatDate(effectiveDate, 'short')} — {formatDate(expiryDate, 'short')}
                </span>
              </div>
            )}

            {(vehicleCount > 0 || driverCount > 0 || coverageCount > 0) && (
              <div className={styles.statCard}>
                <span className={styles.statLabel}>At a Glance</span>
                <div className={styles.glanceRow}>
                  {coverageCount > 0 && (
                    <span className={styles.glanceItem}>
                      <Shield size={14} strokeWidth={2} />
                      {coverageCount} coverage{coverageCount !== 1 ? 's' : ''}
                    </span>
                  )}
                  {vehicleCount > 0 && (
                    <span className={styles.glanceItem}>
                      <Car size={14} strokeWidth={2} />
                      {vehicleCount} vehicle{vehicleCount !== 1 ? 's' : ''}
                    </span>
                  )}
                  {driverCount > 0 && (
                    <span className={styles.glanceItem}>
                      <Users size={14} strokeWidth={2} />
                      {driverCount} driver{driverCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPolicyInfo = () => (
    <div ref={setSectionRef('policy-info')} id="policy-info" className={styles.section}>
      <div className={styles.sectionHeader}>
        <FileText size={20} strokeWidth={2} />
        <h2>Policy Information</h2>
      </div>
      <div className={styles.card}>
        <div className={styles.row}>
          <span className={styles.label}>Insurance Company</span>
          <span className={styles.value}>{metadata.insurance_company?.company_name || 'N/A'}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Policy Number</span>
          <span className={styles.value}>{metadata.policy_metadata?.policy_number || 'N/A'}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Policy Type</span>
          <span className={styles.value}>{formatCoverageType(metadata.policy_metadata?.policy_type)}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Policy Status</span>
          <span className={styles.value}>{formatCoverageType(metadata.policy_metadata?.policy_status)}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Effective Date</span>
          <span className={styles.value}>
            {metadata.policy_metadata?.effective_date 
              ? formatDate(metadata.policy_metadata.effective_date, 'long')
              : 'N/A'}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Expiry Date</span>
          <span className={styles.value}>
            {metadata.policy_metadata?.expiry_date 
              ? formatDate(metadata.policy_metadata.expiry_date, 'long')
              : 'N/A'}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Issue Date</span>
          <span className={styles.value}>
            {metadata.policy_metadata?.issue_date 
              ? formatDate(metadata.policy_metadata.issue_date, 'long')
              : 'N/A'}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Policy Term</span>
          <span className={styles.value}>
            {metadata.policy_metadata?.policy_term_months 
              ? `${metadata.policy_metadata.policy_term_months} months` 
              : 'N/A'}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Jurisdiction</span>
          <span className={styles.value}>
            {metadata.policy_metadata?.jurisdiction?.toUpperCase() || 'N/A'}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Country</span>
          <span className={styles.value}>
            {metadata.policy_metadata?.country?.toUpperCase() || 'N/A'}
          </span>
        </div>
      </div>
    </div>
  );

  const renderCompanyDetails = () => (
    <div ref={setSectionRef('company')} id="company" className={styles.section}>
      <div className={styles.sectionHeader}>
        <Building2 size={20} strokeWidth={2} />
        <h2>Insurance Company Details</h2>
      </div>
      <div className={styles.card}>
        <div className={styles.row}>
          <span className={styles.label}>Company Phone</span>
          <span className={styles.value}>{metadata.insurance_company?.company_phone || 'N/A'}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Claims Phone</span>
          <span className={styles.value}>{metadata.insurance_company?.claims_phone || 'N/A'}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Policy Service Phone</span>
          <span className={styles.value}>{metadata.insurance_company?.policy_service_phone || 'N/A'}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Website</span>
          <span className={styles.value}>
            {metadata.insurance_company?.company_website ? (
              <a 
                href={metadata.insurance_company.company_website} 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.link}
              >
                {metadata.insurance_company.company_website}
              </a>
            ) : 'N/A'}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Address</span>
          <span className={styles.value}>{metadata.insurance_company?.company_address || 'N/A'}</span>
        </div>
      </div>
    </div>
  );

  const renderPremium = () => (
    <div ref={setSectionRef('premium')} id="premium" className={styles.section}>
      <div className={styles.sectionHeader}>
        <CreditCard size={20} strokeWidth={2} />
        <h2>Premium & Billing</h2>
      </div>
      <div className={styles.card}>
        <div className={styles.row}>
          <span className={styles.label}>Total Premium</span>
          <span className={styles.value}>
            {metadata.premium_billing?.total_premium 
              ? formatCurrency(metadata.premium_billing.total_premium)
              : 'N/A'}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Premium Before Tax</span>
          <span className={styles.value}>
            {metadata.premium_billing?.premium_before_tax 
              ? formatCurrency(metadata.premium_billing.premium_before_tax)
              : 'N/A'}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Tax Amount</span>
          <span className={styles.value}>
            {metadata.premium_billing?.tax_amount 
              ? formatCurrency(metadata.premium_billing.tax_amount)
              : 'N/A'}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Payment Frequency</span>
          <span className={styles.value}>
            {formatCoverageType(metadata.premium_billing?.payment_frequency)}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Payment Amount</span>
          <span className={styles.value}>
            {metadata.premium_billing?.payment_amount 
              ? formatCurrency(metadata.premium_billing.payment_amount)
              : 'N/A'}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Payment Method</span>
          <span className={styles.value}>
            {formatCoverageType(metadata.premium_billing?.payment_method)}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>First Payment Date</span>
          <span className={styles.value}>
            {metadata.premium_billing?.first_payment_date 
              ? formatDate(metadata.premium_billing.first_payment_date, 'long')
              : 'N/A'}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Billing Account</span>
          <span className={styles.value}>{metadata.premium_billing?.billing_account_number || 'N/A'}</span>
        </div>
      </div>

      {metadata.premium_billing?.fees?.length > 0 && (
        <div className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>Fees</h3>
          <div className={styles.card}>
            {metadata.premium_billing.fees.map((fee, idx) => (
              <div key={idx} className={styles.row}>
                <span className={styles.label}>{fee.fee_name}</span>
                <span className={styles.value}>{formatCurrency(fee.fee_amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {metadata.premium_billing?.bank_account_info && (
        <div className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>Bank Account</h3>
          <div className={styles.card}>
            <div className={styles.row}>
              <span className={styles.label}>Bank Name</span>
              <span className={styles.value}>{metadata.premium_billing.bank_account_info.bank_name || 'N/A'}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.label}>Account Number</span>
              <span className={styles.value}>{metadata.premium_billing.bank_account_info.account_number_masked || 'N/A'}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.label}>Account Holder</span>
              <span className={styles.value}>{metadata.premium_billing.bank_account_info.account_holder || 'N/A'}</span>
            </div>
          </div>
        </div>
      )}

      {metadata.premium_billing?.payment_schedule?.length > 0 && (
        <div className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>Payment Schedule</h3>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Payment #</th>
                  <th>Date</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {metadata.premium_billing.payment_schedule.map((payment, idx) => (
                  <tr key={idx}>
                    <td>{payment.payment_number || idx + 1}</td>
                    <td>{formatDate(payment.payment_date, 'short')}</td>
                    <td>{formatCurrency(payment.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {metadata.premium_billing?.other_policies_on_account?.length > 0 && (
        <div className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>Other Policies on Account</h3>
          <ul className={styles.list}>
            {metadata.premium_billing.other_policies_on_account.map((policy, idx) => (
              <li key={idx} className={styles.listItem}>
                <FileText size={16} strokeWidth={2} className={styles.listIcon} />
                <span>
                  {policy.policy_number} - {formatCoverageType(policy.policy_type)}
                  {policy.premium && ` (${formatCurrency(policy.premium)})`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const renderCoverages = () => {
    const hasCoverages = metadata.coverages?.coverages && metadata.coverages.coverages.length > 0;

    return (
      <div ref={setSectionRef('coverages')} id="coverages" className={styles.section}>
        <div className={styles.sectionHeader}>
          <Shield size={20} strokeWidth={2} />
          <h2>Coverages {hasCoverages ? `(${metadata.coverages.coverage_count || metadata.coverages.coverages.length})` : ''}</h2>
        </div>
        
        {hasCoverages ? (
          <>
            {metadata.coverages.coverages.map((coverage, idx) => (
              <div key={idx} className={styles.coverageCard}>
                <div className={styles.coverageHeader}>
                  <h3 className={styles.coverageName}>
                    {coverage.coverage_name}
                    {coverage.is_declined && (
                      <span className={`${styles.badge} ${styles.badgeError}`}>
                        <XCircle size={12} strokeWidth={2} />
                        Declined
                      </span>
                    )}
                    {coverage.is_optional && !coverage.is_declined && (
                      <span className={`${styles.badge} ${styles.badgeInfo}`}>
                        Optional
                      </span>
                    )}
                  </h3>
                  <span className={styles.coverageCategory}>
                    {formatCoverageCategory(coverage.coverage_category)}
                  </span>
                </div>

                <div className={styles.card}>
                  {coverage.coverage_code && (
                    <div className={styles.row}>
                      <span className={styles.label}>Coverage Code</span>
                      <span className={styles.value}>{coverage.coverage_code}</span>
                    </div>
                  )}
                  
                  {coverage.coverage_type && (
                    <div className={styles.row}>
                      <span className={styles.label}>Type</span>
                      <span className={styles.value}>{formatCoverageType(coverage.coverage_type)}</span>
                    </div>
                  )}

                  {coverage.limit !== null && coverage.limit !== undefined && (
                    <div className={styles.row}>
                      <span className={styles.label}>Limit</span>
                      <span className={styles.value}>{formatCurrency(coverage.limit)}</span>
                    </div>
                  )}

                  {coverage.sub_limits && Object.values(coverage.sub_limits).some(val => val !== null && val !== undefined) && (
                    <div className={styles.row}>
                      <span className={styles.label}>Sub-Limits</span>
                      <span className={styles.value}>
                        {coverage.sub_limits.per_person && `Per Person: ${formatCurrency(coverage.sub_limits.per_person)}`}
                        {coverage.sub_limits.per_occurrence && ` | Per Occurrence: ${formatCurrency(coverage.sub_limits.per_occurrence)}`}
                        {coverage.sub_limits.per_accident && ` | Per Accident: ${formatCurrency(coverage.sub_limits.per_accident)}`}
                        {coverage.sub_limits.aggregate && ` | Aggregate: ${formatCurrency(coverage.sub_limits.aggregate)}`}
                      </span>
                    </div>
                  )}

                  {coverage.deductible !== null && coverage.deductible !== undefined && (
                    <div className={styles.row}>
                      <span className={styles.label}>Deductible</span>
                      <span className={styles.value}>
                        {coverage.deductible === 0 ? 'No Deductible' : formatCurrency(coverage.deductible)}
                      </span>
                    </div>
                  )}

                  {coverage.premium !== null && coverage.premium !== undefined && (
                    <div className={styles.row}>
                      <span className={styles.label}>Premium</span>
                      <span className={styles.value}>
                        {coverage.premium === 0 ? 'Included' : formatCurrency(coverage.premium)}
                      </span>
                    </div>
                  )}

                  {coverage.applies_to && (
                    <div className={styles.row}>
                      <span className={styles.label}>Applies To</span>
                      <span className={styles.value}>{formatCoverageType(coverage.applies_to)}</span>
                    </div>
                  )}

                  {coverage.coverage_details?.description && (
                    <div className={styles.rowFull}>
                      <span className={styles.label}>Description</span>
                      <span className={styles.value}>{coverage.coverage_details.description}</span>
                    </div>
                  )}

                  {coverage.coverage_details?.jurisdiction_specific_info && 
                   Object.keys(coverage.coverage_details.jurisdiction_specific_info).length > 0 && (
                    <div className={styles.rowFull}>
                      <span className={styles.label}>Additional Details</span>
                      <div className={styles.detailsGrid}>
                        {Object.entries(coverage.coverage_details.jurisdiction_specific_info).map(([key, value]) => {
                          if (value === null || value === undefined) return null;
                          return (
                            <div key={key} className={styles.detailRow}>
                              <span className={styles.detailLabel}>{formatCoverageType(key)}:</span>
                              <span className={styles.detailValue}>
                                {typeof value === 'number' && (key.includes('limit') || key.includes('benefit') || key.includes('amount') || key.includes('weekly'))
                                  ? formatCurrency(value)
                                  : typeof value === 'boolean'
                                  ? value ? 'Yes' : 'No'
                                  : value}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {metadata.coverages.total_coverage_premium && (
              <div className={styles.totalPremium}>
                <span className={styles.label}>Total Coverage Premium:</span>
                <span className={styles.value}>{formatCurrency(metadata.coverages.total_coverage_premium)}</span>
              </div>
            )}
          </>
        ) : (
          <div className={styles.emptyState}>
            <Shield size={48} strokeWidth={2} className={styles.emptyIcon} />
            <p>No coverage information available</p>
          </div>
        )}
      </div>
    );
  };

  const renderEndorsements = () => {
    if (!metadata.endorsements?.endorsements?.length) return null;

    return (
      <div ref={setSectionRef('endorsements')} id="endorsements" className={styles.section}>
        <div className={styles.sectionHeader}>
          <FileText size={20} strokeWidth={2} />
          <h2>Endorsements ({metadata.endorsements.endorsement_count})</h2>
        </div>
        {metadata.endorsements.endorsements.map((end, idx) => (
          <div key={idx} className={styles.card}>
            <div className={styles.row}>
              <span className={styles.label}>Name</span>
              <span className={styles.value}>{end.endorsement_name || 'N/A'}</span>
            </div>
            {end.endorsement_code && (
              <div className={styles.row}>
                <span className={styles.label}>Code</span>
                <span className={styles.value}>{end.endorsement_code}</span>
              </div>
            )}
            {end.endorsement_number && (
              <div className={styles.row}>
                <span className={styles.label}>Number</span>
                <span className={styles.value}>{end.endorsement_number}</span>
              </div>
            )}
            {end.premium !== null && end.premium !== undefined && (
              <div className={styles.row}>
                <span className={styles.label}>Premium</span>
                <span className={styles.value}>{formatCurrency(end.premium)}</span>
              </div>
            )}
            {end.limit !== null && end.limit !== undefined && (
              <div className={styles.row}>
                <span className={styles.label}>Limit</span>
                <span className={styles.value}>{formatCurrency(end.limit)}</span>
              </div>
            )}
            {end.applies_to && (
              <div className={styles.row}>
                <span className={styles.label}>Applies To</span>
                <span className={styles.value}>{formatCoverageType(end.applies_to)}</span>
              </div>
            )}
            {end.effective_date && (
              <div className={styles.row}>
                <span className={styles.label}>Effective Date</span>
                <span className={styles.value}>{formatDate(end.effective_date, 'long')}</span>
              </div>
            )}
            {end.description && (
              <div className={styles.rowFull}>
                <span className={styles.label}>Description</span>
                <span className={styles.value}>{end.description}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderDeclined = () => {
    if (!metadata.declined_coverages?.declined_items?.length) return null;

    return (
      <div ref={setSectionRef('declined')} id="declined" className={styles.section}>
        <div className={styles.sectionHeader}>
          <XCircle size={20} strokeWidth={2} />
          <h2>Declined Coverages ({metadata.declined_coverages.declined_count})</h2>
        </div>
        <ul className={styles.list}>
          {metadata.declined_coverages.declined_items.map((item, idx) => (
            <li key={idx} className={styles.listItem}>
              <XCircle size={16} strokeWidth={2} className={styles.listIconError} />
              <div className={styles.declinedContent}>
                <strong>{item.coverage_name}</strong>
                {item.coverage_code && <span className={styles.coverageCode}>({item.coverage_code})</span>}
                {item.applies_to && <span className={styles.appliesTo}> • Applies to: {formatCoverageType(item.applies_to)}</span>}
                {item.reason_declined && <p className={styles.declineReason}>{item.reason_declined}</p>}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderInsured = () => (
    <div ref={setSectionRef('insured')} id="insured" className={styles.section}>
      <div className={styles.sectionHeader}>
        <User size={20} strokeWidth={2} />
        <h2>Primary Insured</h2>
      </div>
      <div className={styles.card}>
        <div className={styles.row}>
          <span className={styles.label}>Name</span>
          <span className={styles.value}>{metadata.insured_information?.primary_insured?.full_name || 'N/A'}</span>
        </div>
        {metadata.insured_information?.primary_insured?.date_of_birth && (
          <div className={styles.row}>
            <span className={styles.label}>Date of Birth</span>
            <span className={styles.value}>
              {formatDate(metadata.insured_information.primary_insured.date_of_birth, 'long')}
            </span>
          </div>
        )}
        {metadata.insured_information?.primary_insured?.phone && (
          <div className={styles.row}>
            <span className={styles.label}>Phone</span>
            <span className={styles.value}>{metadata.insured_information.primary_insured.phone}</span>
          </div>
        )}
        {metadata.insured_information?.primary_insured?.email && (
          <div className={styles.row}>
            <span className={styles.label}>Email</span>
            <span className={styles.value}>{metadata.insured_information.primary_insured.email}</span>
          </div>
        )}
        {metadata.insured_information?.primary_insured?.address && (
          <div className={styles.rowFull}>
            <span className={styles.label}>Address</span>
            <span className={styles.value}>
              {[
                metadata.insured_information.primary_insured.address.street,
                metadata.insured_information.primary_insured.address.city,
                metadata.insured_information.primary_insured.address.province_state,
                metadata.insured_information.primary_insured.address.postal_zip_code,
                metadata.insured_information.primary_insured.address.country
              ].filter(Boolean).join(', ')}
            </span>
          </div>
        )}
      </div>

      {metadata.insured_information?.additional_insureds?.length > 0 && (
        <div className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>Additional Insureds</h3>
          {metadata.insured_information.additional_insureds.map((insured, idx) => (
            <div key={idx} className={styles.card}>
              <div className={styles.row}>
                <span className={styles.label}>Name</span>
                <span className={styles.value}>{insured.full_name || 'N/A'}</span>
              </div>
              {insured.relationship && (
                <div className={styles.row}>
                  <span className={styles.label}>Relationship</span>
                  <span className={styles.value}>{formatCoverageType(insured.relationship)}</span>
                </div>
              )}
              {insured.date_of_birth && (
                <div className={styles.row}>
                  <span className={styles.label}>Date of Birth</span>
                  <span className={styles.value}>{formatDate(insured.date_of_birth, 'long')}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {metadata.insured_information?.mailing_address_different && metadata.insured_information?.mailing_address && (
        <div className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>Mailing Address</h3>
          <div className={styles.card}>
            <div className={styles.rowFull}>
              <span className={styles.value}>
                {[
                  metadata.insured_information.mailing_address.street,
                  metadata.insured_information.mailing_address.city,
                  metadata.insured_information.mailing_address.province_state,
                  metadata.insured_information.mailing_address.postal_zip_code,
                  metadata.insured_information.mailing_address.country
                ].filter(Boolean).join(', ')}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderVehicles = () => {
    if (!metadata.vehicles?.vehicles?.length) return null;

    return (
      <div ref={setSectionRef('vehicles')} id="vehicles" className={styles.section}>
        <div className={styles.sectionHeader}>
          <Car size={20} strokeWidth={2} />
          <h2>Vehicles ({metadata.vehicles.vehicle_count})</h2>
        </div>
        {metadata.vehicles.vehicles.map((vehicle, idx) => (
          <div key={idx} className={styles.itemCard}>
            <h3 className={styles.itemTitle}>
              Vehicle #{vehicle.vehicle_number}: {vehicle.year} {vehicle.make} {vehicle.model}
              {vehicle.trim && ` ${vehicle.trim}`}
            </h3>
            <div className={styles.card}>
              {vehicle.vin && (
                <div className={styles.row}>
                  <span className={styles.label}>VIN</span>
                  <span className={styles.value}>{vehicle.vin}</span>
                </div>
              )}
              {vehicle.body_type && (
                <div className={styles.row}>
                  <span className={styles.label}>Body Type</span>
                  <span className={styles.value}>{formatCoverageType(vehicle.body_type)}</span>
                </div>
              )}
              {vehicle.registered_owner && (
                <div className={styles.row}>
                  <span className={styles.label}>Registered Owner</span>
                  <span className={styles.value}>{vehicle.registered_owner}</span>
                </div>
              )}
              {vehicle.primary_use && (
                <div className={styles.row}>
                  <span className={styles.label}>Primary Use</span>
                  <span className={styles.value}>{formatCoverageType(vehicle.primary_use)}</span>
                </div>
              )}
              {vehicle.annual_kilometers_miles && (
                <div className={styles.row}>
                  <span className={styles.label}>Annual KM/Miles</span>
                  <span className={styles.value}>{vehicle.annual_kilometers_miles.toLocaleString()}</span>
                </div>
              )}
              {vehicle.ownership_type && (
                <div className={styles.row}>
                  <span className={styles.label}>Ownership</span>
                  <span className={styles.value}>{formatCoverageType(vehicle.ownership_type)}</span>
                </div>
              )}
              {vehicle.purchase_date && (
                <div className={styles.row}>
                  <span className={styles.label}>Purchase Date</span>
                  <span className={styles.value}>{formatDate(vehicle.purchase_date, 'long')}</span>
                </div>
              )}
              {vehicle.purchase_condition && (
                <div className={styles.row}>
                  <span className={styles.label}>Condition</span>
                  <span className={styles.value}>{formatCoverageType(vehicle.purchase_condition)}</span>
                </div>
              )}
              {vehicle.lienholder?.name && (
                <div className={styles.row}>
                  <span className={styles.label}>Lienholder</span>
                  <span className={styles.value}>{vehicle.lienholder.name}</span>
                </div>
              )}
              {vehicle.lessor?.name && (
                <div className={styles.row}>
                  <span className={styles.label}>Lessor</span>
                  <span className={styles.value}>{vehicle.lessor.name}</span>
                </div>
              )}
              {!vehicle.garaging_address_same && vehicle.garaging_address && (
                <div className={styles.rowFull}>
                  <span className={styles.label}>Garaging Address</span>
                  <span className={styles.value}>
                    {[
                      vehicle.garaging_address.street,
                      vehicle.garaging_address.city,
                      vehicle.garaging_address.province_state,
                      vehicle.garaging_address.postal_zip_code
                    ].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderProperties = () => {
    if (!metadata.properties?.properties?.length) return null;

    return (
      <div ref={setSectionRef('properties')} id="properties" className={styles.section}>
        <div className={styles.sectionHeader}>
          <Home size={20} strokeWidth={2} />
          <h2>Properties ({metadata.properties.property_count})</h2>
        </div>
        {metadata.properties.properties.map((property, idx) => (
          <div key={idx} className={styles.itemCard}>
            <h3 className={styles.itemTitle}>
              Property #{property.property_number}
              {property.property_type && `: ${formatCoverageType(property.property_type)}`}
            </h3>
            <div className={styles.card}>
              {property.address && (
                <div className={styles.rowFull}>
                  <span className={styles.label}>Address</span>
                  <span className={styles.value}>
                    {[
                      property.address.street,
                      property.address.city,
                      property.address.province_state,
                      property.address.postal_zip_code,
                      property.address.country
                    ].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
              {property.usage && (
                <div className={styles.row}>
                  <span className={styles.label}>Usage</span>
                  <span className={styles.value}>{formatCoverageType(property.usage)}</span>
                </div>
              )}
              {property.ownership && (
                <div className={styles.row}>
                  <span className={styles.label}>Ownership</span>
                  <span className={styles.value}>{formatCoverageType(property.ownership)}</span>
                </div>
              )}
              {property.year_built && (
                <div className={styles.row}>
                  <span className={styles.label}>Year Built</span>
                  <span className={styles.value}>{property.year_built}</span>
                </div>
              )}
              {property.square_footage && (
                <div className={styles.row}>
                  <span className={styles.label}>Square Footage</span>
                  <span className={styles.value}>{property.square_footage.toLocaleString()} sq ft</span>
                </div>
              )}
              {property.construction_type && (
                <div className={styles.row}>
                  <span className={styles.label}>Construction</span>
                  <span className={styles.value}>{formatCoverageType(property.construction_type)}</span>
                </div>
              )}
              {property.roof_type && (
                <div className={styles.row}>
                  <span className={styles.label}>Roof Type</span>
                  <span className={styles.value}>{formatCoverageType(property.roof_type)}</span>
                </div>
              )}
              {property.roof_age && (
                <div className={styles.row}>
                  <span className={styles.label}>Roof Age</span>
                  <span className={styles.value}>{property.roof_age} years</span>
                </div>
              )}
              {property.number_of_stories && (
                <div className={styles.row}>
                  <span className={styles.label}>Stories</span>
                  <span className={styles.value}>{property.number_of_stories}</span>
                </div>
              )}
              {property.garage_type && (
                <div className={styles.row}>
                  <span className={styles.label}>Garage</span>
                  <span className={styles.value}>{formatCoverageType(property.garage_type)}</span>
                </div>
              )}
              {property.heating_type && (
                <div className={styles.row}>
                  <span className={styles.label}>Heating</span>
                  <span className={styles.value}>{formatCoverageType(property.heating_type)}</span>
                </div>
              )}
            </div>

            {property.safety_features && Object.values(property.safety_features).some(v => v) && (
              <div className={styles.subsection}>
                <h4 className={styles.subsectionTitle}>Safety Features</h4>
                <ul className={styles.list}>
                  {Object.entries(property.safety_features).map(([key, value]) => 
                    value && (
                      <li key={key} className={styles.listItem}>
                        <CheckCircle size={16} strokeWidth={2} className={styles.listIcon} />
                        <span>{formatCoverageType(key)}</span>
                      </li>
                    )
                  )}
                </ul>
              </div>
            )}

            {property.special_features && Object.values(property.special_features).some(v => v && v !== 'none') && (
              <div className={styles.subsection}>
                <h4 className={styles.subsectionTitle}>Special Features</h4>
                <ul className={styles.list}>
                  {Object.entries(property.special_features).map(([key, value]) => 
                    value && value !== 'none' && value !== false && (
                      <li key={key} className={styles.listItem}>
                        <CheckCircle size={16} strokeWidth={2} className={styles.listIcon} />
                        <span>
                          {formatCoverageType(key)}
                          {typeof value === 'string' && value !== 'true' && `: ${formatCoverageType(value)}`}
                        </span>
                      </li>
                    )
                  )}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderDrivers = () => {
    if (!metadata.drivers?.drivers?.length) return null;

    return (
      <div ref={setSectionRef('drivers')} id="drivers" className={styles.section}>
        <div className={styles.sectionHeader}>
          <Users size={20} strokeWidth={2} />
          <h2>Drivers ({metadata.drivers.driver_count})</h2>
        </div>
        {metadata.drivers.drivers.map((driver, idx) => (
          <div key={idx} className={styles.itemCard}>
            <h3 className={styles.itemTitle}>
              Driver #{driver.driver_number}: {driver.full_name}
              {driver.excluded_driver && (
                <span className={`${styles.badge} ${styles.badgeWarning}`}>
                  <AlertCircle size={12} strokeWidth={2} />
                  Excluded
                </span>
              )}
              {driver.occasional_driver && (
                <span className={`${styles.badge} ${styles.badgeInfo}`}>
                  Occasional
                </span>
              )}
            </h3>
            <div className={styles.card}>
              {driver.date_of_birth && (
                <div className={styles.row}>
                  <span className={styles.label}>Date of Birth</span>
                  <span className={styles.value}>{formatDate(driver.date_of_birth, 'long')}</span>
                </div>
              )}
              {driver.gender && (
                <div className={styles.row}>
                  <span className={styles.label}>Gender</span>
                  <span className={styles.value}>{driver.gender}</span>
                </div>
              )}
              {driver.marital_status && (
                <div className={styles.row}>
                  <span className={styles.label}>Marital Status</span>
                  <span className={styles.value}>{formatCoverageType(driver.marital_status)}</span>
                </div>
              )}
              {driver.license_number && (
                <div className={styles.row}>
                  <span className={styles.label}>License Number</span>
                  <span className={styles.value}>{driver.license_number}</span>
                </div>
              )}
              {driver.license_province_state && (
                <div className={styles.row}>
                  <span className={styles.label}>License Province/State</span>
                  <span className={styles.value}>{driver.license_province_state.toUpperCase()}</span>
                </div>
              )}
              {driver.years_licensed && (
                <div className={styles.row}>
                  <span className={styles.label}>Years Licensed</span>
                  <span className={styles.value}>{driver.years_licensed}</span>
                </div>
              )}
              {driver.date_licensed && (
                <div className={styles.row}>
                  <span className={styles.label}>Date Licensed</span>
                  <span className={styles.value}>{formatDate(driver.date_licensed, 'long')}</span>
                </div>
              )}
              {driver.occupation && (
                <div className={styles.row}>
                  <span className={styles.label}>Occupation</span>
                  <span className={styles.value}>{driver.occupation}</span>
                </div>
              )}
              {driver.principal_operator_vehicle && (
                <div className={styles.row}>
                  <span className={styles.label}>Principal Operator</span>
                  <span className={styles.value}>Vehicle #{driver.principal_operator_vehicle}</span>
                </div>
              )}
              {driver.driver_training_certificate !== null && driver.driver_training_certificate !== undefined && (
                <div className={styles.row}>
                  <span className={styles.label}>Driver Training</span>
                  <span className={styles.value}>{driver.driver_training_certificate ? 'Yes' : 'No'}</span>
                </div>
              )}
            </div>

            {driver.convictions && (driver.convictions.minor > 0 || driver.convictions.major > 0 || driver.convictions.serious > 0) && (
              <div className={styles.subsection}>
                <h4 className={styles.subsectionTitle}>Convictions</h4>
                <div className={styles.card}>
                  {driver.convictions.minor > 0 && (
                    <div className={styles.row}>
                      <span className={styles.label}>Minor</span>
                      <span className={styles.value}>{driver.convictions.minor}</span>
                    </div>
                  )}
                  {driver.convictions.major > 0 && (
                    <div className={styles.row}>
                      <span className={styles.label}>Major</span>
                      <span className={styles.value}>{driver.convictions.major}</span>
                    </div>
                  )}
                  {driver.convictions.serious > 0 && (
                    <div className={styles.row}>
                      <span className={styles.label}>Serious</span>
                      <span className={styles.value}>{driver.convictions.serious}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderRating = () => {
    if (!metadata.rating_information) return null;

    return (
      <div ref={setSectionRef('rating')} id="rating" className={styles.section}>
        <div className={styles.sectionHeader}>
          <TrendingUp size={20} strokeWidth={2} />
          <h2>Rating Information</h2>
        </div>
        <div className={styles.card}>
          {metadata.rating_information.territory && (
            <div className={styles.row}>
              <span className={styles.label}>Territory</span>
              <span className={styles.value}>{metadata.rating_information.territory}</span>
            </div>
          )}
          {metadata.rating_information.rate_group && (
            <div className={styles.row}>
              <span className={styles.label}>Rate Group</span>
              <span className={styles.value}>{metadata.rating_information.rate_group}</span>
            </div>
          )}
          {metadata.rating_information.rating_code && (
            <div className={styles.row}>
              <span className={styles.label}>Rating Code</span>
              <span className={styles.value}>{metadata.rating_information.rating_code}</span>
            </div>
          )}
          {metadata.rating_information.parking_type && (
            <div className={styles.row}>
              <span className={styles.label}>Parking Type</span>
              <span className={styles.value}>{formatCoverageType(metadata.rating_information.parking_type)}</span>
            </div>
          )}
          {metadata.rating_information.vehicle_use_class && (
            <div className={styles.row}>
              <span className={styles.label}>Vehicle Use Class</span>
              <span className={styles.value}>{metadata.rating_information.vehicle_use_class}</span>
            </div>
          )}
        </div>

        {metadata.rating_information.discounts?.length > 0 && (
          <div className={styles.subsection}>
            <h3 className={styles.subsectionTitle}>Discounts</h3>
            <ul className={styles.list}>
              {metadata.rating_information.discounts.map((disc, idx) => (
                <li key={idx} className={styles.listItem}>
                  <CheckCircle size={16} strokeWidth={2} className={styles.listIcon} />
                  <div className={styles.discountContent}>
                    <strong>{disc.discount_name}</strong>
                    {disc.discount_code && <span className={styles.code}>({disc.discount_code})</span>}
                    {disc.discount_percentage && <span className={styles.percentage}>{disc.discount_percentage}%</span>}
                    {disc.discount_amount && <span className={styles.amount}>{formatCurrency(disc.discount_amount)}</span>}
                    {disc.applies_to && <span className={styles.appliesTo}>Applies to: {formatCoverageType(disc.applies_to)}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {metadata.rating_information.surcharges?.length > 0 && (
          <div className={styles.subsection}>
            <h3 className={styles.subsectionTitle}>Surcharges</h3>
            <ul className={styles.list}>
              {metadata.rating_information.surcharges.map((surcharge, idx) => (
                <li key={idx} className={styles.listItem}>
                  <AlertCircle size={16} strokeWidth={2} className={styles.listIconWarning} />
                  <div className={styles.surchargeContent}>
                    <strong>{surcharge.surcharge_name}</strong>
                    {surcharge.surcharge_code && <span className={styles.code}>({surcharge.surcharge_code})</span>}
                    {surcharge.surcharge_percentage && <span className={styles.percentage}>+{surcharge.surcharge_percentage}%</span>}
                    {surcharge.surcharge_amount && <span className={styles.amount}>+{formatCurrency(surcharge.surcharge_amount)}</span>}
                    {surcharge.applies_to && <span className={styles.appliesTo}>Applies to: {formatCoverageType(surcharge.applies_to)}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderClaimsHistory = () => {
    if (!metadata.claims_history || (!metadata.claims_history.chargeable_claims?.length && !metadata.claims_history.conviction_history?.length)) return null;

    return (
      <div ref={setSectionRef('claims-history')} id="claims-history" className={styles.section}>
        <div className={styles.sectionHeader}>
          <AlertCircle size={20} strokeWidth={2} />
          <h2>Claims & Convictions History</h2>
        </div>
        
        {metadata.claims_history.chargeable_claims?.length > 0 && (
          <div className={styles.subsection}>
            <h3 className={styles.subsectionTitle}>Claims ({metadata.claims_history.claims_count})</h3>
            {metadata.claims_history.chargeable_claims.map((claim, idx) => (
              <div key={idx} className={styles.card}>
                {claim.claim_date && (
                  <div className={styles.row}>
                    <span className={styles.label}>Date</span>
                    <span className={styles.value}>{formatDate(claim.claim_date, 'long')}</span>
                  </div>
                )}
                {claim.claim_type && (
                  <div className={styles.row}>
                    <span className={styles.label}>Type</span>
                    <span className={styles.value}>{formatCoverageType(claim.claim_type)}</span>
                  </div>
                )}
                {claim.at_fault !== null && claim.at_fault !== undefined && (
                  <div className={styles.row}>
                    <span className={styles.label}>At Fault</span>
                    <span className={styles.value}>{claim.at_fault ? 'Yes' : 'No'}</span>
                  </div>
                )}
                {claim.claim_amount && (
                  <div className={styles.row}>
                    <span className={styles.label}>Amount</span>
                    <span className={styles.value}>{formatCurrency(claim.claim_amount)}</span>
                  </div>
                )}
                {claim.description && (
                  <div className={styles.rowFull}>
                    <span className={styles.label}>Description</span>
                    <span className={styles.value}>{claim.description}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {metadata.claims_history.conviction_history?.length > 0 && (
          <div className={styles.subsection}>
            <h3 className={styles.subsectionTitle}>Convictions</h3>
            {metadata.claims_history.conviction_history.map((conviction, idx) => (
              <div key={idx} className={styles.card}>
                {conviction.conviction_date && (
                  <div className={styles.row}>
                    <span className={styles.label}>Date</span>
                    <span className={styles.value}>{formatDate(conviction.conviction_date, 'long')}</span>
                  </div>
                )}
                {conviction.conviction_type && (
                  <div className={styles.row}>
                    <span className={styles.label}>Type</span>
                    <span className={styles.value}>{formatCoverageType(conviction.conviction_type)}</span>
                  </div>
                )}
                {conviction.driver_name && (
                  <div className={styles.row}>
                    <span className={styles.label}>Driver</span>
                    <span className={styles.value}>{conviction.driver_name}</span>
                  </div>
                )}
                {conviction.description && (
                  <div className={styles.rowFull}>
                    <span className={styles.label}>Description</span>
                    <span className={styles.value}>{conviction.description}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderAgent = () => {
    if (!metadata.agent_information) return null;

    return (
      <div ref={setSectionRef('agent')} id="agent" className={styles.section}>
        <div className={styles.sectionHeader}>
          <Briefcase size={20} strokeWidth={2} />
          <h2>Agent Information</h2>
        </div>
        <div className={styles.card}>
          {metadata.agent_information.agent_name && (
            <div className={styles.row}>
              <span className={styles.label}>Agent Name</span>
              <span className={styles.value}>{metadata.agent_information.agent_name}</span>
            </div>
          )}
          {metadata.agent_information.agency_name && (
            <div className={styles.row}>
              <span className={styles.label}>Agency Name</span>
              <span className={styles.value}>{metadata.agent_information.agency_name}</span>
            </div>
          )}
          {metadata.agent_information.agent_phone && (
            <div className={styles.row}>
              <span className={styles.label}>Phone</span>
              <span className={styles.value}>{metadata.agent_information.agent_phone}</span>
            </div>
          )}
          {metadata.agent_information.agent_email && (
            <div className={styles.row}>
              <span className={styles.label}>Email</span>
              <span className={styles.value}>{metadata.agent_information.agent_email}</span>
            </div>
          )}
          {metadata.agent_information.broker_name && (
            <div className={styles.row}>
              <span className={styles.label}>Broker Name</span>
              <span className={styles.value}>{metadata.agent_information.broker_name}</span>
            </div>
          )}
          {metadata.agent_information.agent_address && (
            <div className={styles.rowFull}>
              <span className={styles.label}>Address</span>
              <span className={styles.value}>{metadata.agent_information.agent_address}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAdditional = () => {
    if (!metadata.additional_details) return null;

    return (
      <div ref={setSectionRef('additional')} id="additional" className={styles.section}>
        <div className={styles.sectionHeader}>
          <Sparkles size={20} strokeWidth={2} />
          <h2>Additional Details</h2>
        </div>
        
        {metadata.additional_details.membership_affiliations?.length > 0 && (
          <div className={styles.subsection}>
            <h3 className={styles.subsectionTitle}>Memberships</h3>
            <ul className={styles.list}>
              {metadata.additional_details.membership_affiliations.map((membership, idx) => (
                <li key={idx} className={styles.listItem}>
                  <CheckCircle size={16} strokeWidth={2} className={styles.listIcon} />
                  <span>{membership}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {metadata.additional_details.special_programs?.length > 0 && (
          <div className={styles.subsection}>
            <h3 className={styles.subsectionTitle}>Special Programs</h3>
            <ul className={styles.list}>
              {metadata.additional_details.special_programs.map((program, idx) => (
                <li key={idx} className={styles.listItem}>
                  <CheckCircle size={16} strokeWidth={2} className={styles.listIcon} />
                  <span>{program}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={styles.card}>
          {metadata.additional_details.telematics_enrolled !== null && metadata.additional_details.telematics_enrolled !== undefined && (
            <div className={styles.row}>
              <span className={styles.label}>Telematics Enrolled</span>
              <span className={styles.value}>{metadata.additional_details.telematics_enrolled ? 'Yes' : 'No'}</span>
            </div>
          )}
          {metadata.additional_details.paperless_enrolled !== null && metadata.additional_details.paperless_enrolled !== undefined && (
            <div className={styles.row}>
              <span className={styles.label}>Paperless Enrolled</span>
              <span className={styles.value}>{metadata.additional_details.paperless_enrolled ? 'Yes' : 'No'}</span>
            </div>
          )}
          {metadata.additional_details.auto_renewal !== null && metadata.additional_details.auto_renewal !== undefined && (
            <div className={styles.row}>
              <span className={styles.label}>Auto Renewal</span>
              <span className={styles.value}>{metadata.additional_details.auto_renewal ? 'Yes' : 'No'}</span>
            </div>
          )}
        </div>

        {metadata.additional_details.pets?.length > 0 && (
          <div className={styles.subsection}>
            <h3 className={styles.subsectionTitle}>Pets</h3>
            <ul className={styles.list}>
              {metadata.additional_details.pets.map((pet, idx) => (
                <li key={idx} className={styles.listItem}>
                  <span className={styles.petIcon}>🐾</span>
                  <span>
                    {formatCoverageType(pet.pet_type)}
                    {pet.breed && ` - ${pet.breed}`}
                    {pet.count && pet.count > 1 && ` (${pet.count})`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderNotes = () => {
    if (!metadata.extraction_notes) return null;

    return (
      <div ref={setSectionRef('notes')} id="notes" className={styles.section}>
        <div className={styles.sectionHeader}>
          <AlertCircle size={20} strokeWidth={2} />
          <h2>Extraction Notes</h2>
        </div>
        
        {metadata.extraction_notes.warnings?.length > 0 && (
          <div className={styles.subsection}>
            <h3 className={styles.subsectionTitle}>⚠️ Warnings</h3>
            <ul className={styles.notesList}>
              {metadata.extraction_notes.warnings.map((warning, idx) => (
                <li key={idx}>{warning}</li>
              ))}
            </ul>
          </div>
        )}

        {metadata.extraction_notes.data_quality_issues?.length > 0 && (
          <div className={styles.subsection}>
            <h3 className={styles.subsectionTitle}>Data Quality Issues</h3>
            <ul className={styles.notesList}>
              {metadata.extraction_notes.data_quality_issues.map((issue, idx) => (
                <li key={idx}>{issue}</li>
              ))}
            </ul>
          </div>
        )}

        {metadata.extraction_notes.recommendations?.length > 0 && (
          <div className={styles.subsection}>
            <h3 className={styles.subsectionTitle}>💡 Recommendations</h3>
            <ul className={styles.notesList}>
              {metadata.extraction_notes.recommendations.map((rec, idx) => (
                <li key={idx}>{rec}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.commandCenter}>
      {/* Main content area */}
      <div className={styles.contentArea} ref={contentRef}>
        {renderSummary()}
        {renderPolicyInfo()}
        {renderCompanyDetails()}
        {renderPremium()}
        {renderCoverages()}
        {renderEndorsements()}
        {renderDeclined()}
        {renderInsured()}
        {renderVehicles()}
        {renderProperties()}
        {renderDrivers()}
        {renderRating()}
        {renderClaimsHistory()}
        {renderAgent()}
        {renderAdditional()}
        {renderNotes()}
      </div>

      {/* Floating sidebar - desktop/tablet */}
      <aside className={styles.floatingSidebar}>
        <div className={styles.sidebarInner} data-lenis-prevent>
          {/* Navigation section */}
          <nav className={styles.sidebarNav}>
            <span className={styles.sidebarLabel}>Sections</span>
            <div className={styles.navItems}>
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    className={`${styles.navItem} ${activeSection === section.id ? styles.navItemActive : ''}`}
                    onClick={() => scrollToSection(section.id)}
                    title={section.label}
                  >
                    <Icon size={16} strokeWidth={2} />
                    <span className={styles.navLabel}>{section.label}</span>
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Divider */}
          <div className={styles.sidebarDivider} />

          {/* Actions section */}
          <div className={styles.sidebarActions}>
            <span className={styles.sidebarLabel}>Actions</span>
            <div className={styles.actionItems}>
              <button
                className={`${styles.actionItem} ${styles.actionChat}`}
                onClick={onStartPolicyChat}
                disabled={isStartingPolicyChat}
                title="Ask about your policy"
              >
                {isStartingPolicyChat ? (
                  <Loader2 size={16} strokeWidth={2} className={styles.spinning} />
                ) : (
                  <MessageCircle size={16} strokeWidth={2} />
                )}
                <span className={styles.actionLabel}>Ask AI</span>
              </button>

              <button
                className={`${styles.actionItem} ${styles.actionClaims}`}
                onClick={onStartClaimsChat}
                disabled={isStartingClaimsChat}
                title="File a claim"
              >
                {isStartingClaimsChat ? (
                  <Loader2 size={16} strokeWidth={2} className={styles.spinning} />
                ) : (
                  <FileCheck size={16} strokeWidth={2} />
                )}
                <span className={styles.actionLabel}>Claim</span>
              </button>

              <button
                className={`${styles.actionItem} ${styles.actionQuote}`}
                onClick={onStartQuoteChat}
                disabled={isStartingQuoteChat}
                title="Get a quote"
              >
                {isStartingQuoteChat ? (
                  <Loader2 size={16} strokeWidth={2} className={styles.spinning} />
                ) : (
                  <DollarSign size={16} strokeWidth={2} />
                )}
                <span className={styles.actionLabel}>Quote</span>
              </button>

              <button
                className={`${styles.actionItem} ${styles.actionDelete}`}
                onClick={onDelete}
                title="Delete policy"
              >
                <Trash2 size={16} strokeWidth={2} />
                <span className={styles.actionLabel}>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile bottom bar */}
      <div className={styles.mobileBottomBar}>
        <div className={styles.mobileNavScroll}>
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                className={`${styles.mobileNavItem} ${activeSection === section.id ? styles.mobileNavItemActive : ''}`}
                onClick={() => scrollToSection(section.id)}
              >
                <Icon size={16} strokeWidth={2} />
                <span>{section.label}</span>
              </button>
            );
          })}
        </div>
        <div className={styles.mobileActions}>
          <button
            className={`${styles.mobileActionBtn} ${styles.actionChat}`}
            onClick={onStartPolicyChat}
            disabled={isStartingPolicyChat}
          >
            {isStartingPolicyChat ? (
              <Loader2 size={18} strokeWidth={2} className={styles.spinning} />
            ) : (
              <MessageCircle size={18} strokeWidth={2} />
            )}
          </button>
          <button
            className={`${styles.mobileActionBtn} ${styles.actionClaims}`}
            onClick={onStartClaimsChat}
            disabled={isStartingClaimsChat}
          >
            {isStartingClaimsChat ? (
              <Loader2 size={18} strokeWidth={2} className={styles.spinning} />
            ) : (
              <FileCheck size={18} strokeWidth={2} />
            )}
          </button>
          <button
            className={`${styles.mobileActionBtn} ${styles.actionQuote}`}
            onClick={onStartQuoteChat}
            disabled={isStartingQuoteChat}
          >
            {isStartingQuoteChat ? (
              <Loader2 size={18} strokeWidth={2} className={styles.spinning} />
            ) : (
              <DollarSign size={18} strokeWidth={2} />
            )}
          </button>
          <button
            className={`${styles.mobileActionBtn} ${styles.actionDelete}`}
            onClick={onDelete}
          >
            <Trash2 size={18} strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* Scroll to top */}
      {showScrollTop && (
        <button className={styles.scrollTopBtn} onClick={scrollToTop} title="Scroll to top">
          <ChevronUp size={20} strokeWidth={2} />
        </button>
      )}
    </div>
  );
};

export default PolicyCommandCenter;